import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { AlertTriangle, CalendarClock, Loader2, XCircle, RotateCcw, ExternalLink } from 'lucide-react';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from '../ui/alert-dialog';

const fmtDate = (iso) => {
  if (!iso) return 'the end of the period';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return 'the end of the period'; }
};

/**
 * Cancel / resume / dunning banners + Stripe portal link.
 * Shared by BillingHome and Settings > Subscription. `sub` is the
 * /subscription/current response. Change/Upgrade is handled by the host page.
 */
export default function SubscriptionActions({ sub, accountId, onChanged }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [portalUrl, setPortalUrl] = useState(null);
  const subscription = sub?.subscription || {};
  const plan = sub?.plan || {};
  const isPaid = plan.id && plan.id !== 'free';
  const canceling = !!subscription.cancel_at_period_end;
  const pastDue = subscription.status === 'past_due';

  useEffect(() => {
    if (!accountId) return;
    api.get('/subscription/portal-link', { params: { account_id: accountId } })
      .then((r) => setPortalUrl(r.data?.url || null))
      .catch(() => {});
  }, [accountId]);

  const doCancel = async () => {
    setBusy('cancel');
    try {
      await api.post('/subscription/cancel', { account_id: accountId, at_period_end: true });
      toast.success('Cancellation scheduled for period end');
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cancel failed'); }
    finally { setBusy(null); }
  };

  const doResume = async () => {
    setBusy('resume');
    try {
      await api.post('/subscription/resume', { account_id: accountId });
      toast.success('Your plan will continue');
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Resume failed'); }
    finally { setBusy(null); }
  };

  if (!isPaid && !pastDue) return null;

  return (
    <div className="space-y-3" data-testid="subscription-actions">
      {pastDue && (
        <div className="bg-rose-50 border border-rose-200 p-4 flex items-center gap-3" data-testid="payment-failed-banner">
          <AlertTriangle className="h-5 w-5 text-rose-600 flex-shrink-0" />
          <div className="flex-1 text-sm text-rose-800">
            Your last renewal failed and your plan is <b>past due</b>. Pay now to avoid being downgraded to Free.
          </div>
          <Button onClick={() => navigate('/billing/choose')} className="rounded-none" data-testid="payment-failed-update-btn">Fix billing</Button>
        </div>
      )}

      {canceling && !pastDue && (
        <div className="bg-amber-50 border border-amber-200 p-4 flex items-center gap-3" data-testid="cancel-scheduled-banner">
          <CalendarClock className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <div className="flex-1 text-sm text-amber-800">
            Your plan is set to cancel on <b>{fmtDate(subscription.current_period_end)}</b>. You'll move to Free after that.
          </div>
          <Button onClick={doResume} disabled={busy === 'resume'} className="rounded-none" data-testid="resume-plan-btn">
            {busy === 'resume' ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RotateCcw className="h-4 w-4 mr-2" />Resume</>}
          </Button>
        </div>
      )}

      {isPaid && (
        <div className="flex items-center gap-2">
          {!canceling && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button disabled={busy === 'cancel'} variant="outline"
                        className="rounded-none text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
                        data-testid="cancel-plan-btn">
                  {busy === 'cancel' ? <Loader2 className="h-4 w-4 animate-spin" /> : <><XCircle className="h-4 w-4 mr-2" />Cancel plan</>}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="cancel-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel your {plan.label} plan?</AlertDialogTitle>
                  <AlertDialogDescription>
                    You'll keep full access until {fmtDate(subscription.current_period_end)}, then move to the Free plan.
                    You can resume anytime before then.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-none" data-testid="cancel-confirm-no">Keep plan</AlertDialogCancel>
                  <AlertDialogAction onClick={doCancel} className="rounded-none bg-rose-600 hover:bg-rose-700" data-testid="cancel-confirm-yes">
                    Cancel at period end
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {portalUrl && (
            <a href={portalUrl} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-primary hover:underline"
               data-testid="billing-portal-link">
              Manage billing <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}
